import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styles from './News.module.css';
import { createApiUrl } from '../utils/apiConfig';

const News = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredNews, setFilteredNews] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [email, setEmail] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');

  useEffect(() => {
    // Function to load published articles and categories from database
    const loadData = async () => {
      try {
        const [articlesResponse, categoriesResponse] = await Promise.all([
          axios.get(createApiUrl('/api/news/articles/published')),
          axios.get(createApiUrl('/api/news/categories'))
        ]);
        setNews(articlesResponse.data);
        setFilteredNews(articlesResponse.data);
        setCategories(categoriesResponse.data);
      } catch (error) {
        console.error('Error loading data:', error);
        setNews([]);
        setFilteredNews([]);
        setCategories([]);
      }
    };

    // Load data initially
    loadData();
  }, []);

  useEffect(() => {
    let filtered = news;
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.content && item.content.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    setFilteredNews(filtered);
  }, [news, selectedCategory, searchTerm]);

  // Create categories array with dynamic categories plus "All News"
  const allCategories = [
    { id: 'all', name: 'All News' },
    ...categories.map(cat => ({ id: cat.name, name: cat.displayName }))
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSubscribe = async () => {
    if (!email) {
      setSubscriptionStatus('Please enter an email address');
      return;
    }

    try {
      const response = await axios.post(createApiUrl('/api/subscribe'), { email });
      if (response.status === 200) {
        setSubscriptionStatus('Successfully subscribed!');
        setEmail('');
        // Set a cookie to remember the subscription
        document.cookie = 'subscribed=true; max-age=604800; path=/';
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setSubscriptionStatus('An error occurred while subscribing. Please try again.');
    }
  };

  return (
    <div className={styles.newsPage}>
      <div className={styles.newsMainContent}>
        {/* Hero Section */}
        <section className={styles.newsHero}>
          <div className={styles.container}>
            <h1>Newsroom</h1>
            <p>Stay updated with the latest news, announcements, and stories from KBH Classroom</p>
          </div>
        </section>

      {/* Filters and Search */}
      <section className={styles.newsFilters}>
        <div className={styles.container}>
          <div className={styles.filtersWrapper}>
            <div className={styles.searchBox}>
              <input
                type="text"
                placeholder="Search news..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <i className="fas fa-search"></i>
            </div>
            
            <div className={styles.categoryFilters}>
              {allCategories.map(category => (
                <button
                  key={category.id}
                  className={`${styles.categoryBtn} ${selectedCategory === category.id ? styles.active : ''}`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className={styles.newsGrid}>
        <div className={styles.container}>
          {filteredNews.length === 0 ? (
            <div className={styles.noResults}>
              <h3>No news found</h3>
              <p>Try adjusting your search or filter criteria.</p>
            </div>
          ) : (
            <div className={styles.newsCards}>
              {filteredNews.map(article => (
                <article key={article._id} className={styles.newsCard}>
                  <div className={styles.cardImage}>
                    <img src={article.coverImage || (article.images && article.images.length > 0 ? article.images[0] : '/news-images/news1.svg')} alt={article.title} />
                    <div className={styles.cardCategory}>{article.category}</div>
                  </div>
                  
                  <div className={styles.cardContent}>
                    <div className={styles.cardMeta}>
                      <span className={styles.cardDate}>{formatDate(article.createdAt)}</span>
                      <span className={styles.cardReadTime}>{article.readTime}</span>
                    </div>
                    
                    <h3 className={styles.cardTitle}>{article.title}</h3>
                    <p className={styles.cardExcerpt}>{article.excerpt}</p>
                    
                    <div className={styles.cardFooter}>
                      <span className={styles.cardAuthor}>By {article.author}</span>
                      <button 
                        className={styles.readMoreBtn}
                        onClick={() => navigate(`/news/${article._id}`)}
                      >
                        Read More
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className={styles.newsletterSignup}>
        <div className={styles.container}>
          <div className={styles.newsletterContent}>
            <h2>Stay Updated</h2>
            <p>Subscribe to our newsletter to receive the latest news and updates directly in your inbox.</p>
            <div className={styles.newsletterForm}>
              <input 
                type="email" 
                placeholder="Enter your email address" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button type="button" onClick={handleSubscribe}>Subscribe</button>
            </div>
            {subscriptionStatus && (
              <div className={`${styles.subscriptionStatus} ${subscriptionStatus.includes('Successfully') ? styles.success : styles.error}`}>
                {subscriptionStatus}
              </div>
            )}
          </div>
        </div>
      </section>
      </div>
    </div>
  );
};

export default News; 